import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Box } from 'lucide-react';

const Navbar = ({ scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Construction', path: '/construction' },
    { name: 'Leisure', path: '/leisure' },
    { name: 'Healthcare', path: '/healthcare' },
    { name: 'Commercial', path: '/commercial-property-3d-tours-london' },
    { name: 'CGI', path: '/cgi' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Case Studies', path: '/case-studies' },
    { name: 'Insights', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group relative" aria-label="Digital Scanning Group home page">
            {/* Hologram glow effect */}
            <div className={`absolute inset-0 blur-xl opacity-0 group-hover:opacity-60 transition-opacity duration-500 ${
              scrolled ? 'bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600' : 'bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-500'
            }`}></div>

            <img
              src="/images/logo-160.webp"
              srcSet="/images/logo-160.webp 2x, /images/logo-240.webp 3x"
              width="80"
              height="80"
              alt="Digital Scanning Group logo - 3D scanning and digital twin specialists"
              className={`h-20 w-auto relative z-10 transition-all duration-300 group-hover:scale-105 ${
                scrolled ? '' : 'drop-shadow-[0_0_8px_rgba(34,211,238,0.8)]'
              }`}
              style={{
                filter: scrolled ? 'none' : 'drop-shadow(0 0 12px rgba(96, 165, 250, 0.6))',
                imageRendering: 'crisp-edges'
              }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                aria-label={`Navigate to ${link.name}`}
                className={`text-sm font-medium transition-all duration-300 hover:scale-105 ${
                  location.pathname === link.path
                    ? scrolled
                      ? 'text-blue-600'
                      : 'text-white font-semibold'
                    : scrolled
                    ? 'text-gray-700 hover:text-blue-600'
                    : 'text-white/90 hover:text-white'
                }`}
              >
                {link.name}
              </Link>
            ))}
            {/* CTA Button */}
            <button
              onClick={scrollToContact}
              className="ml-2 px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold rounded-full hover:shadow-lg hover:shadow-cyan-500/50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              aria-label="Get a quote for 3D scanning services"
            >
              Get a Quote
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden p-2 transition-colors ${
              scrolled ? 'text-gray-900' : 'text-white'
            }`}
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-xl">
          <div className="px-6 py-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                aria-label={`Navigate to ${link.name}`}
                className={`block py-2 text-sm font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-blue-600'
                    : 'text-gray-700 hover:text-blue-600'
                }`}
              >
                {link.name}
              </Link>
            ))}
            {/* Mobile CTA */}
            <button
              onClick={scrollToContact}
              className="w-full mt-4 px-5 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-sm font-semibold rounded-full hover:shadow-lg transition-all duration-300"
              aria-label="Get a quote for 3D scanning services"
            >
              Get a Quote
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
